import { FileUpload } from "@/components/ui/file-upload";
import { SelectField } from "@/features/FilterTable/SelectField";
import { getAuthorized, postFormAuthorized } from "@/lib/fetch";
import { DownloadButton } from "@/components/DownloadButton";
import { Button } from "@heroui/react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ConfigSchemaType, UploadSchemaType } from "./filter";

type Mode = "create" | "modify";

type S2DataUploadNewProps = {
  setIsOpen: (val: boolean) => void;
  files: File[];
  setFiles: (files: File[]) => void;
  selectedConfig: string | null;
  setSelectedConfig: (config: string | null) => void;
  mode?: Mode;
  fileId?: number;
  onSuccess?: () => Promise<void> | void;
};

export function S2DataUploadNew({
  setIsOpen,
  files,
  setFiles,
  selectedConfig,
  setSelectedConfig,
  mode = "create",
  fileId,
  onSuccess,
}: S2DataUploadNewProps) {
  const { t } = useTranslation();

  const [configs, setConfigs] = useState<ConfigSchemaType[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { setValue, watch } = useForm<UploadSchemaType>();
  const configValue = watch("config_name");

  // 🔄 Fetch available configs on mount
  useEffect(() => {
    setSelectedConfig(configValue || null);
  }, [configValue, setSelectedConfig]);

  useEffect(() => {
    if (selectedConfig) {
      setValue("config_name", selectedConfig);
    }
  }, [selectedConfig, setValue]);

  useEffect(() => {
    const fetchConfigs = async () => {
      try {
        const res = await getAuthorized(
          `${import.meta.env.VITE_BACKEND_URL}/s2/s2_data_upload/configs`,
        );
        const data = await res.json();
        setConfigs(data); // expects an array of strings
      } catch (err) {
        console.error("Error fetching configs:", err);
        setError(t("data_upload.errors.load_configurations"));
      }
    };

    fetchConfigs();
  }, [t]);

  const handleEdit = async () => {
    if (!files.length || files.length !== 1) {
      setError(t("data_upload.errors.select_single_file"));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const formData = new FormData();
    formData.append("file", files[0]);

    try {
      await postFormAuthorized(
        `${import.meta.env.VITE_BACKEND_URL}/s2/s2_data_upload/edit/${fileId}`, // adjust endpoint if needed
        formData,
      );

      await onSuccess?.();
      setIsOpen(false);
      toast.success(t("data_upload.success.file_modified"));
      setFiles([]);
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        t("data_upload.errors.modify_failed");
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedConfig) {
      setError(t("data_upload.errors.select_configuration"));
      return;
    }

    if (!files.length) {
      setError(t("data_upload.errors.select_files"));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const selectedConfigObj = configs.find((c) => c.code === selectedConfig);

    if (!selectedConfigObj) {
      setError(t("data_upload.errors.invalid_configuration"));
      return;
    }

    if (!selectedConfigObj.mime_type) {
      setError(t("data_upload.errors.missing_file_type"));
      return;
    }

    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    formData.append("config_name", selectedConfig);
    formData.append("config_id", String(selectedConfigObj.id));

    try {
      await postFormAuthorized(
        `${import.meta.env.VITE_BACKEND_URL}/s2/s2_data_upload/upload`,
        formData,
      );

      await onSuccess?.();
      setIsOpen(false);
      toast.success(t("data_upload.success.files_sent"));
      setFiles([]);
      setSelectedConfig(null);
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        t("data_upload.errors.send_failed");
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = async () => {
    setIsOpen(false);
    setFiles([]);
    setSelectedConfig(null);
  };

  const acceptedTypes = selectedConfig
    ? [configs.find((c) => c.code === selectedConfig)?.mime_type || ""]
    : [];

  const template_file_id = selectedConfig
    ? configs.find((c) => c.code === selectedConfig)?.template_file_id
    : "";

  const template_file_name = selectedConfig
    ? configs.find((c) => c.code === selectedConfig)?.template_file_name
    : "";

  return (
    <div className="flex justify-center items-start w-full px-4 py-6">
      <div className="flex flex-col w-full max-w-card rounded-lg p-6 gap-4 bg-white">
        {mode === "create" ? (
          <SelectField<UploadSchemaType>
            key="config_name"
            field={{
              name: "config_name",
              label: t("data_upload.fields.configuration"),
              type: "select",
              useTranslation: false, // set to true if using translations
              options: configs.map((c) => ({
                value: c.code,
                label: c.code,
              })),
            }}
            setValue={setValue}
            watch={watch}
          />
        ) : (
          ""
        )}
        {acceptedTypes && (
          <div className="flex justify-between items-center text-sm text-gray-600">
            {acceptedTypes?.length > 0 && (
              <span>
                {t("data_upload.labels.accepted_types")}{" "}
                <span className="text-primary">{acceptedTypes.join(", ")}</span>
              </span>
            )}
            {template_file_id &&
              (() => {
                if (!template_file_id) return null;

                const href = `${import.meta.env.VITE_BACKEND_URL}/s2/s2_data_upload/download/${template_file_id}`;

                return (
                  <DownloadButton
                    href={href}
                    filename={template_file_name}
                    size="sm"
                    variant="light"
                    color="primary"
                    text={t("data_upload.actions.template")}
                  />
                );
              })()}
          </div>
        )}
        <FileUpload
          files={files}
          setFiles={setFiles}
          setError={setError}
          acceptedTypes={acceptedTypes}
          maxFiles={mode === "modify" ? 1 : undefined}
        />
        <footer className="flex justify-end gap-2">
          <Button
            color="primary"
            radius="full"
            size="sm"
            onPress={mode === "create" ? handleSubmit : handleEdit}
            disabled={isSubmitting || !files?.length}
            className="disabled:opacity-50"
          >
            {isSubmitting ? t("actions.uploading") : t("actions.confirm")}
          </Button>
          <Button
            color="primary"
            radius="full"
            variant="flat"
            size="sm"
            onPress={handleReset}
          >
            {t("actions.cancel")}
          </Button>
        </footer>
        {error && <p className="text-sm text-red-500">{error}</p>}
      </div>
    </div>
  );
}
